import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { size, head } from 'lodash';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { modelOf } from '../../../prop-types';
import Product from '../../../models/Product';
import ProductLink from '../ProductLink';
import ProductImage from '../ProductImage';
import ProductReviewWidget from '../ProductReviewWidget';
import Analytics from '../../../analytics/Analytics';
import ImagePreload from '../../common/ImagePreload';
import ProductCardProperties from '../ProductCardProperties';
import ProductImageSigns from '../ProductImageSigns';
import ProductClass from '../../../types/ProductClass';
import ProductPropertyType from '../../../types/ProductPropertyType';
import ProductAddToCart from '../ProductAddToCart';
import ConfigStore from '../../../store/ConfigStore';
import ProductId from '../ProductId';
import UIStore from '../../../store/UIStore';
import ProductCardPropertiesStatic from '../ProductCardPropertiesStatic';
import globalTranslations from '../../../i18n/globalTranslations';
import AccountStore from '../../../store/AccountStore';
import ProductEnquiry from '../../product-enquiry/ProductEnquiry';
import ProductEnquiryType from '../../../types/ProductEnquiryType';
import ProductPackageSize from '../ProductPackageSize';
import PriceWrapper from '../PriceWrapper';
import ProductSingularPrice from '../ProductSingularPrice';
import ProductFeatureImages from '../ProductFeatureImages';
import ProductLabelType from '../../../types/ProductLabelType';
import PriceSecondary from '../PriceSecondary';
import ExternalReviewType from '../../../types/ExternalReviewType';
import ShoppingCenterProductMerchantInfo from '../../shopping-center/ShoppingCenterProductMerchantInfo';
import ProductDescription from '../ProductDescription';
import ProductDeposit from '../ProductDeposit';

const ProductCollectionPropertyTypeHeights = new Map()
  .set(ProductPropertyType.COLOR, 60)
  .set(ProductPropertyType.OTHER, 35)
  .set(ProductPropertyType.SIZE, 35);

@observer
export class ProductCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      secondaryImageActive: false,
      secondaryImageLoaded: false,
      selectedElementId: null,
      hoverProperties: false,
      quantity: 1
    };
  }

  onEventStart = () => {
    this.enableSecondaryImage();
    // mobile uses static properties
    if (!this.props.uiStore.isMobile) {
      this.enableProperties();
    }
  };

  onEventEnd = () => {
    this.disableSecondaryImage();
    if (!this.props.uiStore.isMobile) {
      this.disableProperties();
    }
  };

  enableSecondaryImage = () => {
    if (!this.state.secondaryImageActive) {
      this.setState({ secondaryImageActive: true });
    }
  };

  disableSecondaryImage = () => {
    if (this.state.secondaryImageActive) {
      this.setState({ secondaryImageActive: false, selectedElementId: null });
    }
  };

  enableProperties = () => {
    if (!this.state.hoverProperties) {
      this.setState({ hoverProperties: true });
    }
  };

  disableProperties = () => {
    if (this.state.hoverProperties) {
      this.setState({ hoverProperties: false, selectedElementId: null });
    }
  };

  handleProductClick = () => {
    const { analytics, position, listName, product } = this.props;
    analytics.productClick(listName, [
      {
        product,
        position
      }
    ]);
  };

  onSecondaryImagePreload = () => {
    this.setState({
      secondaryImageLoaded: true
    });
  };

  onFirstCollectionElementHovered = (hoveredElementId) => {
    this.setState({ selectedElementId: hoveredElementId });
  };

  productCollectionTotalHeight = () => {
    let height = 0;
    const {
      product: { collection }
    } = this.props;
    if (
      collection &&
      collection.column &&
      collection.column.id !== 0 &&
      collection.column.elements.length > 0 &&
      collection.column.elements[0].id !== 0
    ) {
      height += ProductCollectionPropertyTypeHeights.get(
        collection.column.type
      );
    }
    if (
      collection &&
      collection.row &&
      collection.row.id !== 0 &&
      collection.row.elements.length > 0 &&
      collection.row.elements[0].id !== 0
    ) {
      height += ProductCollectionPropertyTypeHeights.get(collection.row.type);
    }
    return height;
  };

  isColumnCollectionElementId = (product, elementId) => {
    return product.collection.column.elements.some((el) => el.id === elementId);
  };

  getMobileProperties = () => {
    const { product } = this.props;
    return <ProductCardPropertiesStatic product={product} />;
  };

  getAvailability = (showProperties) => {
    const { product } = this.props;
    const availability = product.listingAvailability;

    if (
      availability.length === 0 ||
      (product.class === ProductClass.COLLECTION && showProperties)
    ) {
      return null;
    }

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: availability
        }}
      />
    );
  };

  getLabelsBelowTitle = () => {
    const { product } = this.props;
    const textLabels = product.labels.filter(
      (label) =>
        label.show_on_product_listings &&
        label.placement === 'product_properties' &&
        label.display_style !== ProductLabelType.IMAGE
    );

    const imageLabels = product.labels.filter(
      (label) =>
        label.show_on_product_listings &&
        label.placement === 'product_properties' &&
        label.display_style === ProductLabelType.IMAGE
    );

    if (textLabels.length === 0 && imageLabels.length === 0) {
      return null;
    }

    let imageLabelHtml = null;
    if (imageLabels.length > 0) {
      imageLabelHtml = (
        <div className="ProductCard__LabelImageContainer">
          {imageLabels.map((label, index) => (
            <img
              alt=""
              key={index}
              className="ProductCard__LabelImage"
              src={label.image}
              loading="lazy"
            />
          ))}
        </div>
      );
    }

    return (
      <Fragment>
        {imageLabelHtml && imageLabelHtml}
        <ul className="ProductCard__LabelList">
          {textLabels.map((labelObject, index) => {
            const { label } = labelObject;
            const label_text = label ? label.label_text : null;
            const label_value = label ? label.label_value : null;

            return (
              <li
                key={index}
                className={classNames('ProductCard__LabelListItem', {
                  'ProductCard__LabelListItem--withImage': labelObject.image
                })}
              >
                {labelObject.image && (
                  <img
                    alt=""
                    className="ProductCard__LabelImage"
                    src={labelObject.image}
                    loading="lazy"
                  />
                )}
                {label_text && (
                  <strong className="ProductLabel__propertyName">
                    {label.label_text}
                  </strong>
                )}
                {label_value && (
                  <span className="ProductLabel__propertyValue">
                    : {label_value}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </Fragment>
    );
  };

  getWitViewRating = () => {
    const { product } = this.props;

    if (!product.external_reviews) {
      return;
    }

    return product.external_reviews.find(
      ({ type }) => type === ExternalReviewType.WITVIEW
    );
  };

  shouldUseWitViewRating = () => {
    const { configStore } = this.props;

    if (configStore.witView.enabled) {
      const witViewRating = this.getWitViewRating();
      if (witViewRating && witViewRating.reviews_count === 0) {
        return false;
      }

      return true;
    }

    return false;
  };

  getProductQuantity = (quantity) => {
    this.setState({ quantity });
  };

  getProductManufacturerId = () => {
    const { product } = this.props;
    const manufacturer = product.manufacturer;
    return product && manufacturer && manufacturer.id;
  };

  renderShoppingCenterProductMerchantInfo = () => {
    const { product } = this.props;
    const merchantInfo = product.merchantInfo;

    return <ShoppingCenterProductMerchantInfo merchantInfo={merchantInfo} />;
  };

  ifShowModel = () => {
    const { configStore, product } = this.props;

    if (product.class === ProductClass.MULTI) {
      return (
        configStore.productPage.showModelName && configStore.product.showModel
      );
    }

    return configStore.product.showModel;
  };

  renderProductInformation = () => {
    const { configStore, product } = this.props;
    let hasContent = false;

    if (configStore.productCard.showProductCode) {
      hasContent = true;
    }
    if (configStore.productCard.showEanCode && product.ean) {
      hasContent = true;
    }
    if (configStore.productCard.showExtraId && product.extra_id) {
      hasContent = true;
    }
    if (
      configStore.productCard.showManufacturersCode &&
      product.manufacturer_product_id
    ) {
      hasContent = true;
    }

    if (!hasContent) {
      return null;
    }

    return (
      <div className="ProductCard__information">
        {configStore.productCard.showProductCode && (
          <span className="ProductCard__productId">
            <ProductId id={product.id} />
          </span>
        )}
        {configStore.productCard.showEanCode &&
          product.ean && (
            <FormattedMessage
              {...globalTranslations.eanLabel}
              values={{ manufacturerCode: product.manufacturer_product_id }}
            />
          )}
        {configStore.productCard.showExtraId &&
          product.extra_id && (
            <FormattedMessage
              {...globalTranslations.extraIdLabel}
              values={{ extraId: product.extra_id }}
            />
          )}
        {configStore.productCard.showManufacturersCode &&
          product.manufacturer_product_id && (
            <FormattedMessage
              {...globalTranslations.manufacturerCodeLabel}
              values={{ manufacturerCode: product.manufacturer_product_id }}
            />
          )}
      </div>
    );
  };

  render() {
    const {
      product,
      configStore,
      large,
      hideDescription,
      hidePrice,
      hideProperties,
      target,
      modelMargin,
      uiStore,
      accountStore,
      hideQuantity,
      hideAddToCartButton,
      showAsSold,
      disableLinks,
      productListView,
      productParams,
      requireCanonicalLink
    } = this.props;
    const {
      secondaryImageActive,
      selectedElementId,
      secondaryImageLoaded
    } = this.state;

    const doHideDescription =
      hideDescription ||
      !configStore.productCard.showDescription ||
      accountStore.isViewOnly;
    let doHidePrice =
      product.isProposalType(
        product.id,
        configStore.product.enquireForProposal
      ) ||
      hidePrice ||
      accountStore.isViewOnly;
    const doHideProperties = hideProperties || accountStore.isViewOnly;
    const doHideReviews = accountStore.isViewOnly;

    const hideAddToCart =
      hideAddToCartButton ||
      product.has_required_services ||
      !configStore.productCard.showAddToCart;

    const productImage = selectedElementId
      ? product.images.find((i) => i.for_color_id === selectedElementId) ||
        head(product.images)
      : head(product.images);

    const secondaryImage =
      size(product.images) > 1 &&
      !(
        product.class === ProductClass.COLLECTION &&
        !!product.images.find((i) => i.for_color_id !== null)
      )
        ? product.images[1]
        : null;
    const imageSize = large ? 'large' : 'medium';
    let secondaryImagePreload = '';
    let productLinkParams = {};
    // If first collection element is selected, link will contain this selection as parameter
    if (selectedElementId) {
      if (this.isColumnCollectionElementId(product, selectedElementId)) {
        productLinkParams = { columnId: selectedElementId };
      } else {
        productLinkParams = { rowId: selectedElementId };
      }
    }

    productLinkParams = { ...productLinkParams, ...productParams };

    /* This is used to load secondary image when user hovers over image. Secondary image is shown when it is loaded (see below) */
    if (secondaryImage && secondaryImageActive) {
      secondaryImagePreload = (
        <ImagePreload
          src={secondaryImage.sizes['medium']}
          onImageLoad={this.onSecondaryImagePreload}
        />
      );
    }

    const showProperties =
      !doHideProperties && configStore.productCard.showProperties;
    const propertiesHeight = showProperties
      ? this.productCollectionTotalHeight()
      : 0;
    const depositInPrice =
      configStore.productPage.depositInPrice &&
      product.bottle_deposit &&
      product.bottle_deposit.with_tax > 0;

    const price = (
      <PriceWrapper product={product} depositInPrice={depositInPrice} />
    );

    if (price && !hidePrice) {
      doHidePrice = false;
    }

    const productTitle = product.multiproduct_title || product.name;

    const quantityAllowed =
      accountStore.isRetailer || configStore.productCard.showQuantitySelection;

    const renderProductImage = () => {
      const { lazyLoading, productImageSettings } = this.props;
      return (
        <div className={classNames('ProductCard__images-wrapper')}>
          {secondaryImagePreload}
          <ProductImage
            product={product}
            size={imageSize}
            productImage={productImage}
            lazyLoading={lazyLoading}
            {...productImageSettings}
          />
          {secondaryImage && (
            <div
              className={classNames('ProductCard__secondary-image-wrapper', {
                'ProductCard__secondary-image-wrapper--image-loaded': secondaryImageLoaded,
                'ProductCard__secondary-image-wrapper--image-touched':
                  secondaryImageLoaded && secondaryImageActive
              })}
              onMouseOver={this.onEventStart}
              onTouchStart={this.onEventStart}
              onMouseLeave={this.onEventEnd}
              onTouchEnd={this.onEventEnd}
            >
              <ProductImage
                product={product}
                size={imageSize}
                productImage={secondaryImage}
                lazyLoading={lazyLoading}
                {...productImageSettings}
              />
            </div>
          )}
          <div className="ProductCard__data-over-image">
            <ProductFeatureImages
              productListView={productListView}
              product={product}
              position={'product_image'}
            />
            <ProductImageSigns product={product} />
          </div>
        </div>
      );
    };

    const renderProductTitleAndModel = () => {
      return (
        <Fragment>
          <h2>{productTitle}</h2>
          {this.ifShowModel() &&
            product.model && (
              <div
                className={classNames('ProductCard__model', {
                  'ProductCard__model-margin': !!modelMargin
                })}
              >
                {product.model}
              </div>
            )}
        </Fragment>
      );
    };

    const useWitViewRating = this.shouldUseWitViewRating();
    const ifShoppingCenter = configStore.siteConfig.isShoppingCenter;
    const ifWebStore = configStore.siteConfig.isWebStore;
    const merchantInfo = product.merchantInfo;
    const ifBuyingAllowed = merchantInfo && merchantInfo.buyingAllowed;

    if (ifShoppingCenter && !ifBuyingAllowed) {
      doHidePrice = true;
    }
    return (
      <article
        className={classNames('ProductCard', this.getProductManufacturerId())}
      >
        {!disableLinks ? (
          <ProductLink
            product={product}
            target={target}
            onClick={this.handleProductClick}
            params={productParams}
            requireCanonical={requireCanonicalLink}
          >
            {renderProductImage()}
          </ProductLink>
        ) : (
          renderProductImage()
        )}
        <div
          className={classNames('ProductCard__product-data-wrapper', {
            [`ProductCard__product-data-wrapper--margin-top-${propertiesHeight}`]:
              showProperties && !uiStore.isMobile
          })}
        >
          <div className="ProductCard__manufacturer">
            {product.manufacturer && product.manufacturer.name}
            &nbsp;
          </div>
          {!disableLinks ? (
            <ProductLink
              product={product}
              params={productLinkParams}
              onClick={this.handleProductClick}
              target={target}
              requireCanonical={requireCanonicalLink}
            >
              {renderProductTitleAndModel()}
            </ProductLink>
          ) : (
            renderProductTitleAndModel()
          )}
          {!doHideDescription &&
            configStore.product.shortDescriptionTop && (
              <ProductDescription
                className="ProductCardDescription"
                product={product}
                overflowHandler={this.handleProductClick}
              />
            )}
          {this.getLabelsBelowTitle()}
          {this.renderProductInformation()}
          {!uiStore.isMobile && (
            <div className="ProductCard__properties">
              {showProperties && (
                <ProductCardProperties
                  product={product}
                  onFirstCollectionElementHovered={
                    this.onFirstCollectionElementHovered
                  }
                  selectedElementId={selectedElementId}
                />
              )}
            </div>
          )}
          {!doHideReviews &&
            !configStore.siteConfig.isShoppingCenter && (
              <ProductReviewWidget
                product={product}
                useWitViewRating={useWitViewRating}
                compact
              />
            )}
          {!doHidePrice && (
            <div
              className={classNames('ProductCard__price-and-cart', {
                'ProductCard__price-and-cart-with-quantity': quantityAllowed
              })}
            >
              <div className="ProductCard__price-and-availability">
                {!doHidePrice && price}
                {!doHidePrice &&
                  product && (
                    <ProductDeposit
                      product={product}
                      showPrice={!configStore.productPage.depositInPrice}
                    />
                  )}
                {!doHidePrice && (
                  <PriceSecondary product={product} isProductCard />
                )}
                <ProductSingularPrice
                  product={product}
                  hideSingular={!configStore.productCard.showSingularPrice}
                />
                {configStore.siteConfig.isWebStore && (
                  <div className="ProductCard__availability">
                    {this.getAvailability(showProperties)}
                  </div>
                )}
              </div>
              {quantityAllowed &&
                product.sellInPackage && (
                  <ProductPackageSize product={product} />
                )}
              {!accountStore.isViewOnly &&
                !hideAddToCart &&
                !hideAddToCartButton &&
                product.class === ProductClass.SINGLE && (
                  <ProductAddToCart
                    product={product}
                    addToCartProduct={product}
                    activeProductId={product.id}
                    hideQuantity={hideQuantity}
                    quantity={this.state.quantity}
                    productListView
                    simple
                    onUpdateProductQuantity={this.getProductQuantity}
                    ifWebStore={ifWebStore}
                  />
                )}
            </div>
          )}
          {showAsSold &&
            doHidePrice && (
              <span className="ProductCard__sold-out">
                <FormattedMessage id="product.sold" defaultMessage="Sold" />
              </span>
            )}
          {doHidePrice &&
            product.isProposalType(
              product.id,
              configStore.product.enquireForProposal
            ) && (
              <ProductEnquiry
                id={product.id}
                type={ProductEnquiryType.PROPOSAL_REQUEST}
              />
            )}
          {!doHideDescription &&
            configStore.product.shortDescriptionBottom && (
              <ProductDescription
                className="ProductCardDescription"
                product={product}
                overflowHandler={this.handleProductClick}
              />
            )}
          {showProperties &&
            uiStore.isMobile && (
              <div className="ProductCard__properties--static">
                {this.getMobileProperties()}
              </div>
            )}
          {product.suitability_description && (
            <div className="ProductCard__suitability-description">
              <ProductDescription
                className="ProductCardDescription"
                product={product}
                isSuitability={true}
              />
            </div>
          )}
          {this.renderShoppingCenterProductMerchantInfo()}
        </div>
      </article>
    );
  }
}

ProductCard.propTypes = {
  product: modelOf(Product).isRequired,
  listName: PropTypes.string,
  position: PropTypes.number,
  large: PropTypes.bool,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  hidePrice: PropTypes.bool,
  hideDescription: PropTypes.bool,
  hideProperties: PropTypes.bool,
  hideQuantity: PropTypes.bool,
  hideAddToCartButton: PropTypes.bool,
  modelMargin: PropTypes.bool,
  target: PropTypes.string,
  showAsSold: PropTypes.bool,
  disableLinks: PropTypes.bool,
  productImageSettings: PropTypes.object,
  accountStore: modelOf(AccountStore).isRequired,
  productListView: PropTypes.bool, // Card is used in product list
  requireCanonicalLink: PropTypes.bool,
  lazyLoading: PropTypes.bool
};

ProductCard.defaultProps = {
  hideQuantity: false,
  requireCanonicalLink: false,
  lazyLoading: true
};

export default inject('analytics', 'configStore', 'uiStore', 'accountStore')(
  ProductCard
);



// WEBPACK FOOTER //
// ./src/components/product/ProductCard/index.jsx